import { useEffect, useMemo, useState } from 'react';
import { Money } from '@repo/domain';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { currencySymbol, displayWireMoney, parseUserMoney } from '@/features/money';
import { cn } from '@/lib/utils';

/**
 * Data the dialog reads on open and writes on Apply. Matches the
 * subset of `InvoiceLineItemFormValues` the dialog edits — discount
 * and tax stay outside (they have their own focused dialogs
 * accessible from the row pills). Notes + taxable are intentionally
 * absent: mobile omits per-line notes from the line schema, and the
 * `taxable` flag is set implicitly (true) since the tax mutex makes
 * a per-line exempt toggle redundant under invoice-wide tax modes.
 */
export interface LineItemDraft {
  description: string;
  quantity: string;
  unitPriceInput: string;
  unit: string;
}

interface LineItemDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** `null` opens the dialog for a brand-new line; otherwise edit. */
  initial: LineItemDraft | null;
  currency: string;
  /**
   * Surface the "save to library" checkbox. Only meaningful for new
   * items entered freehand (not edits, not services-from-library).
   */
  allowSaveToLibrary?: boolean;
  onSave: (draft: LineItemDraft, opts: { saveToLibrary: boolean }) => void;
}

/**
 * Same catalogue mobile uses (`apps/mobile/.../styles.ts:COMMON_UNITS`).
 * Free-text would let users diverge across invoices for no benefit;
 * chips keep the rendered invoice consistent.
 */
const COMMON_UNITS = [
  'item',
  'hour',
  'day',
  'visit',
  'job',
  'project',
  'fee',
  'unit',
  'sq ft',
];

export function LineItemDialog({
  open,
  onOpenChange,
  initial,
  currency,
  allowSaveToLibrary,
  onSave,
}: LineItemDialogProps) {
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPriceInput, setUnitPriceInput] = useState('');
  const [unit, setUnit] = useState('item');
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof LineItemDraft, string>>>({});

  useEffect(() => {
    if (!open) return;
    setDescription(initial?.description ?? '');
    setQuantity(initial?.quantity ?? '1');
    setUnitPriceInput(initial?.unitPriceInput ?? '');
    setUnit(initial?.unit && initial.unit.trim() !== '' ? initial.unit : 'item');
    setSaveToLibrary(false);
    setErrors({});
  }, [open, initial]);

  // Live line total. Mirrors mobile's "Line total" field at the
  // bottom of the item sheet so the user gets immediate feedback as
  // they type. Falls back to zero when the inputs aren't parseable
  // (mid-edit) rather than blanking out.
  const livePreview = useMemo(() => {
    const parsed = parseUserMoney(unitPriceInput, currency);
    const qtyNum = Number(quantity);
    if (!parsed.ok || !Number.isFinite(qtyNum) || qtyNum <= 0) {
      return displayWireMoney(Money.zero(currency).amount.toString(), currency);
    }
    try {
      const total = Money.mul(parsed.value, qtyNum);
      return displayWireMoney(total.amount.toString(), currency);
    } catch {
      return displayWireMoney(Money.zero(currency).amount.toString(), currency);
    }
  }, [unitPriceInput, quantity, currency]);

  function handleApply() {
    const next: Partial<Record<keyof LineItemDraft, string>> = {};
    if (description.trim() === '') next.description = 'Required';
    const qtyNum = Number(quantity);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) next.quantity = 'Must be > 0';
    const priceTrim = unitPriceInput.trim();
    if (priceTrim === '') {
      next.unitPriceInput = 'Required';
    } else if (!parseUserMoney(priceTrim, currency).ok) {
      next.unitPriceInput = 'Enter a valid amount';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    onSave(
      {
        description: description.trim(),
        quantity: quantity.trim(),
        unitPriceInput: priceTrim,
        unit: unit.trim() || 'item',
      },
      { saveToLibrary: !!allowSaveToLibrary && saveToLibrary },
    );
    onOpenChange(false);
  }

  const symbol = currencySymbol(currency);
  // Show the saved unit as its own chip when it's outside the
  // catalogue — handles legacy "fixed" units and any custom strings
  // already on the wire.
  const allUnitChips = useMemo(() => {
    if (COMMON_UNITS.includes(unit)) return COMMON_UNITS;
    return [unit, ...COMMON_UNITS];
  }, [unit]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit line' : 'New item'}</DialogTitle>
          <DialogDescription>
            {initial
              ? 'Update the line. Discount and tax stay editable from the row.'
              : 'A one-off item, or save to your services library for reuse.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <FieldLabel label="Description" htmlFor="li-description" error={errors.description}>
            <Input
              id="li-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What did you deliver?"
              autoFocus
            />
          </FieldLabel>

          <div className="grid grid-cols-[1fr_2fr] gap-3">
            <FieldLabel label="Quantity" htmlFor="li-qty" error={errors.quantity}>
              <Input
                id="li-qty"
                type="number"
                min={0}
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="text-right tabular-nums"
              />
            </FieldLabel>
            <FieldLabel label={`Unit price · ${currency}`} htmlFor="li-price" error={errors.unitPriceInput}>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  {symbol}
                </span>
                <Input
                  id="li-price"
                  inputMode="decimal"
                  value={unitPriceInput}
                  onChange={(e) => setUnitPriceInput(e.target.value)}
                  placeholder="0.00"
                  className="pl-7 text-right font-mono tabular-nums"
                />
              </div>
            </FieldLabel>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              How is it priced?
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {allUnitChips.map((u) => {
                const on = unit === u;
                return (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setUnit(u)}
                    className={cn(
                      'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      on
                        ? 'bg-foreground text-background'
                        : 'bg-secondary/60 text-foreground hover:bg-secondary',
                    )}
                  >
                    {u}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-baseline justify-between rounded-lg border border-line bg-secondary/30 px-3 py-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Line total
            </span>
            <span className="font-display text-xl font-semibold tabular-nums text-foreground">
              {livePreview}
            </span>
          </div>

          {allowSaveToLibrary ? (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={saveToLibrary}
                onCheckedChange={(v) => setSaveToLibrary(v === true)}
              />
              Save to services library for next time
            </label>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>{initial ? 'Save changes' : 'Add item'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </Label>
      {children}
      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}
